/**
 * A lista de Originados da SDR (história 3.11) — o que ela originou, e no que deu (FR-35).
 *
 * Ela é a tela que fecha o buraco que o handoff abria: feito o `PUT /negocios/{id}/etapa` para uma
 * Etapa de Vendas, o Negócio sai do funil da SDR e, até aqui, sumia do sistema para quem o
 * originou — não havia rota, contrato nem tela que respondesse "o que aconteceu com o que eu
 * entreguei". Agora há uma, e ela mostra o **desfecho** e o **Motivo da perda**, que é exatamente o
 * que o requisito pede.
 *
 * **A visibilidade é do serviço, e esta tela não decide nada sobre ela.** As linhas já chegam
 * recortadas: quem as entrega é a policy `negocio_leitura_dos_originados`, que só alcança o Negócio
 * cujo campo SDR é quem está pedindo, e só na transação que o caso de uso marca. Um `filter` aqui
 * seria a mesma regra escrita num segundo lugar, e os dois divergiriam no primeiro ajuste.
 *
 * **Não há campo de dinheiro, e a ausência não é recorte.** Ao contrário do card do quadro (3.8),
 * da faixa do painel (3.9) e da ficha (3.10) — onde `valor` some para a SDR por chave ausente —,
 * aqui o contrato do serviço **não tem** campo de valor para perfil nenhum (FR-62). Não há três
 * estados a distinguir e não há "valor não visível para SDR" a escrever: não há coluna.
 *
 * **A linha não abre a ficha, e a ausência de link é decisão.** `GET /negocios/{id}` não liga a
 * marca da transação, e a ficha de um Negócio já entregue responde `404`. Linkar produziria um link
 * que funciona enquanto o Negócio está na Prospecção e quebra depois do handoff — pior que não
 * linkar. Se um dia a SDR puder abrir o que entregou, é decisão de produto, e não desta tela.
 *
 * **O nome da Empresa é resolvido aqui, com o JWT de quem está olhando** (AD-25), em **lote** —
 * nunca uma consulta por linha. E a ausência de um nome tem as mesmas **quatro** causas que o
 * painel do dia separa: o lote está no ar (`pendente`), o lote falhou (`indisponivel`), a policy do
 * `cadastro` não devolveu aquela ficha (`ausente`, a degradação documentada do FR-30) ou o nome veio
 * (`resolvido`). Só a terceira afirma alguma coisa, e nenhuma delas tira a linha da tela: o título
 * cai no **código** do Negócio, que é o identificador que a operação já usa na boca (AD-12).
 *
 * **O Motivo da perda é resolvido pela mesma escada de três degraus da ficha**, e não por uma
 * segunda: `useOpcoesDeDominio` (o vocabulário **ativo**) → `useRotulosDeOpcoes` (os ids avulsos,
 * um a um, que resolve inclusive o que a gestão excluiu depois — FR-67) → o rótulo genérico. O
 * serviço manda o uuid cru porque `negocios` não conhece o vocabulário do `cadastro`, consulta
 * síncrona no caminho do request é proibida (AD-22) e a réplica por evento (AD-23) não existe.
 *
 * **O desfecho é um chip por `data-status`**, e nunca uma comparação de texto de tela: o valor vem
 * do contrato (`em_andamento`/`ganho`/`perdido`) e o CSS mapeia os três por seletor de atributo. E
 * a cor não carrega o significado sozinha — o rótulo por extenso está escrito dentro dela, no mesmo
 * elemento.
 *
 * **A linha mostra empresa **e** código, e as duas datas.** São as seis colunas do critério de
 * aceite da 3.11: quem originou pergunta "o que aconteceu com o 4471" pelo código, e "quando
 * fechou" é metade da resposta que a tela existe para dar. O código aparece **sempre**, e não só
 * quando a razão social falta; a conclusão do Negócio ainda em andamento é o mesmo travessão que a
 * coluna do Motivo escreve, porque o `CHECK` de coerência do desfecho a proíbe ali.
 *
 * **Duas árvores, escolhidas por `useEhCelular()` — e não um `@media`.** No computador é uma tabela
 * de seis colunas; no celular, um cartão empilhado por Negócio. Toda informação que existe a
 * 1280px continua alcançável a 375px (NFR-1), e o que garante isso é o **view-model**: as duas
 * árvores desenham a mesma `LinhaDoOriginado`, e os dois pedaços que carregam texto —
 * `TituloDaLinha` e `ChipDoDesfecho` — são escritos **uma** vez e usados pelas duas. O que muda é
 * só o arranjo.
 *
 * **Os textos e os formatadores moram em `apresentacao.ts`**, e não aqui: `ROTULO_DO_STATUS` e o
 * formatador de data são os **mesmos** desta tela e da ficha, e duplicá-los faria a badge da ficha
 * dizer uma coisa e a coluna da lista, outra, com as duas suítes verdes.
 *
 * **Sem paginação e sem teto**, e a ausência é herdada (DW-154): o serviço devolve a lista inteira,
 * e quem originou muito rola. Inventar um teto aqui seria um corte silencioso — a lista não tem,
 * como o painel do dia tem, um número ao lado dizendo de quantos ela é o recorte.
 *
 * **O que esta tela não faz**, e a ausência é o recorte da história: ela não move, não fecha, não
 * edita e não exclui. As três policies de escrita do Negócio não foram tocadas pela 3.11 — a SDR
 * não mexe no que entregou —, e um botão aqui receberia `404` do serviço.
 */

import type { ReactNode } from 'react'
import { useCallback, useMemo } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useNomesDeEmpresas, useOpcoesDeDominio, useRotulosDeOpcoes } from '@/modulos/cadastro/api'
import { useResponsaveis } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import type { NegocioOriginado } from '@/modulos/negocios/api'
import { useOriginados } from '@/modulos/negocios/api'
import {
  COLUNAS_DOS_ORIGINADOS,
  DATA,
  EMPRESA_A_DEFINIR,
  MOTIVO_FORA_DA_LISTA,
  ROTULO_DO_STATUS,
  SEM_DATA_DE_CONCLUSAO,
  SEM_MOTIVO_DE_PERDA,
  SEM_ORIGINADOS,
} from '@/modulos/negocios/apresentacao'
import { useEhCelular } from '@/shell/Shell'
import { LinkDoNegocio } from './navegacao-do-negocio'
import './lista-de-originados.css'

/**
 * O nome acessível da lista, para quem navega por leitor de tela.
 *
 * Ele é **exportado** porque a suíte da tela o usa para achar a tabela e os cartões: redigitado
 * lá, ele passaria a ser a opinião do teste sobre o nome, e renomear a lista aqui deixaria a
 * suíte procurando um nome que a tela não escreve mais — verde num `findByRole` que nunca
 * encontraria nada seria impossível, mas o contrário (dois nomes divergentes, os dois "certos")
 * é exatamente o que o cabeçalho deste módulo proíbe.
 *
 * Ele **não** sobe para `apresentacao.ts`: lá moram os textos que mais de uma tela escreve, e
 * este é de uma só.
 */
export const NOME_DA_LISTA = 'Negócios originados'
/** O cartão da tela, com as palavras do protótipo v2. */
export const TITULO_DO_CARTAO = 'Negócios que você originou'
export const NOTA_DO_CARTAO = 'sem cifras, por desenho'

const CARREGANDO = 'Carregando os negócios originados…'
const TENTAR_DE_NOVO = 'Tentar de novo'

/**
 * O aviso de que os nomes das Empresas não vieram — e que o código na linha **não** quer dizer
 * "esta ficha não é sua".
 *
 * Ele existe porque a degradação silenciosa é indistinguível do recorte de visibilidade: uma linha com
 * o código é legítima e rara (FR-30, "é responsável, vê"); uma lista inteira delas, por causa de um `GET /empresas`
 * recusado, é defeito. É o mesmo aviso do painel do dia, e o mesmo motivo.
 */
const NOMES_NAO_VIERAM =
  'Não foi possível carregar os nomes das empresas; as linhas mostram o código do negócio.'

/** O que o leitor de tela ouve enquanto o nome da Empresa ainda está no ar. */
const NOME_A_CAMINHO = ' (carregando o nome da empresa)'

/** Quando a equipe não resolve o uuid (desligado, ou a lista não veio): o travessão de sempre. */
const NOME_INDISPONIVEL = '—'

/**
 * Por que o título da linha não é a razão social — as **quatro** causas, separadas.
 *
 * É o mesmo tipo de `PainelDeHoje` e de `QuadroDoFunil`, repetido aqui em vez de importado pela
 * mesma razão que aqueles dois o repetem entre si: um `export` faria uma tela depender da vizinha
 * para existir. A separação que importa — `carregando` ≠ `falhou` ≠ "não é sua" — vem de
 * `NomesDeEmpresas`, que é a fonte comum das três.
 */
/*
 * 13.20b2: `sem-empresa` é o Negócio sem Empresa — o título é "Empresa a definir". A linha dos
 * Originados não traz o contato (`RespostaDeNegocioOriginado` não tem `pessoa_id`, contrato da
 * 13.20b1), e a coluna "Empresa" diz o que se sabe dela; o código está na coluna ao lado.
 */
type OrigemDoTitulo = 'resolvido' | 'pendente' | 'indisponivel' | 'ausente' | 'sem-empresa'

/**
 * O que a tela desenha por linha — o view-model, montado uma vez para as duas árvores.
 *
 * Ele existe para que computador e celular escrevam **o mesmo texto**: dois cálculos paralelos
 * seriam duas chances de o celular mostrar o uuid do Motivo onde o computador mostra o rótulo, e a
 * paridade da NFR-1 viraria coincidência.
 *
 * **Sem campo de dinheiro**, e a ausência atravessa daqui: o contrato não tem `valor`, então não há
 * nem o slot recortado que o view-model do painel carrega.
 */
interface LinhaDoOriginado {
  id: string
  /** A razão social; o código do Negócio quando ela não veio (por qualquer das outras causas). */
  titulo: string
  /** Qual das quatro causas produziu o título acima. */
  origemDoTitulo: OrigemDoTitulo
  /**
   * O código do Negócio, sempre — e não só quando o nome falta.
   *
   * Ele é o identificador que a operação usa na boca (AD-12), e é por ele que a SDR pergunta
   * "o que aconteceu com o 4471" a quem ficou com o Negócio. Sem coluna própria ele só apareceria
   * no caso degradado, que é o **menos** frequente.
   */
  codigo: string
  /** O nome da SDR que originou (11.5) — a visão da gestão identifica cada uma. */
  sdr: string
  /** O nome do Responsável **atual** (11.5): "em andamento com quem". */
  responsavel: string
  /** A data de início, já formatada no fuso de quem trabalha. */
  inicio: string
  /**
   * A data de conclusão formatada, ou o travessão enquanto o Negócio está em andamento.
   *
   * As duas datas viajam juntas porque é o par que responde a pergunta da tela: quando entrou e
   * quando fechou. O em andamento não tem a segunda por definição — o `CHECK` de coerência do
   * desfecho a proíbe —, e o travessão é o **mesmo** da coluna do Motivo.
   */
  conclusao: string
  /** O `status` cru do contrato — é ele que pinta o chip, e nunca o texto ao lado. */
  status: NegocioOriginado['status']
  /** O rótulo por extenso do desfecho, o **mesmo** da badge da ficha. */
  desfecho: string
  /** O rótulo do Motivo da perda, ou o travessão quando não há Motivo. */
  motivo: string
}

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** O título da linha, com o aviso de "ainda estou esperando o nome" quando for o caso. */
function TituloDaLinha({ linha }: { linha: LinhaDoOriginado }) {
  const pendente = linha.origemDoTitulo === 'pendente'
  /* A Empresa é link e abre o Negócio em modal sobre a lista (protótipo v2, `.link-nome`). */
  return (
    <LinkDoNegocio
      negocioId={linha.id}
      className="originados__link"
      //: O código no nome acessível (code review, B12): duas linhas "Empresa a definir" — ou duas
      //: Empresas homônimas — não viram dois links de mesmo nome.
      aria-label={`Abrir ${linha.titulo}, negócio ${linha.codigo}`}
    >
      <span
        className="originados__titulo"
        data-nome={linha.origemDoTitulo}
        aria-busy={pendente || undefined}
      >
        {linha.titulo}
        {pendente ? <span className="originados__so-para-leitor">{NOME_A_CAMINHO}</span> : null}
      </span>
    </LinkDoNegocio>
  )
}

/**
 * O chip do desfecho — pintado por `data-status`, e nunca por comparação de texto de tela.
 *
 * O valor vem do contrato, e o CSS mapeia os três por seletor de atributo — os **três**, e não
 * dois mais um padrão: um `status` novo no serviço cai no fundo neutro do `.originados__chip`, que
 * é de ninguém, em vez de se disfarçar de "em andamento". E o rótulo por extenso está **dentro** do
 * chip, e não ao lado dele: cor nunca é o único portador de significado — o texto vem do
 * view-model, que já tem o plano B do valor cru para o mesmo caso.
 */
function ChipDoDesfecho({ linha }: { linha: LinhaDoOriginado }) {
  return (
    <span className="originados__chip" data-status={linha.status}>
      {linha.desfecho}
    </span>
  )
}

/**
 * Uma célula do cartão do celular: o rótulo da coluna mais o valor.
 *
 * O rótulo só aparece aqui porque no computador quem nomeia a coluna é o `<th>`, e repeti-lo em
 * cada linha seria ruído visual e verbal. O texto é o **mesmo** dos dois lados
 * (`COLUNAS_DOS_ORIGINADOS`), e é isso que impede o celular chamar de "Início" o que a tabela chama
 * de "Data de início".
 */
function CampoDoCartao({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="originados__campo">
      <span className="originados__campo-rotulo">{rotulo}</span>
      <span className="originados__campo-valor">{children}</span>
    </div>
  )
}

/** Uma linha da tabela do computador. */
function LinhaDaTabela({ linha }: { linha: LinhaDoOriginado }) {
  return (
    <tr className="originados__linha">
      <td className="originados__celula">
        <TituloDaLinha linha={linha} />
      </td>
      <td className="originados__celula originados__celula--codigo numero">{linha.codigo}</td>
      <td className="originados__celula">{linha.sdr}</td>
      <td className="originados__celula">{linha.responsavel}</td>
      <td className="originados__celula originados__celula--data numero">{linha.inicio}</td>
      <td className="originados__celula originados__celula--data numero">{linha.conclusao}</td>
      <td className="originados__celula">
        <ChipDoDesfecho linha={linha} />
      </td>
      <td className="originados__celula originados__celula--motivo">{linha.motivo}</td>
    </tr>
  )
}

/** Um cartão empilhado do celular — a mesma informação, em outro arranjo. */
function CartaoDoOriginado({ linha }: { linha: LinhaDoOriginado }) {
  return (
    <li className="originados__cartao" data-status={linha.status}>
      {/* Empresa e Desfecho ficam no topo **sem** rótulo: a razão social e o chip se nomeiam
          sozinhos, e uma linha "EMPRESA" em caixa-alta acima do nome seria ruído verbal — o que
          `COLUNAS_DOS_ORIGINADOS` documenta. Os quatro campos empilhados abaixo levam os deles,
          porque ali o valor sozinho não diz de que coluna é. */}
      <div className="originados__cartao-topo">
        <TituloDaLinha linha={linha} />
        <ChipDoDesfecho linha={linha} />
      </div>
      <CampoDoCartao rotulo={COLUNAS_DOS_ORIGINADOS.codigo}>
        <span className="numero">{linha.codigo}</span>
      </CampoDoCartao>
      <CampoDoCartao rotulo={COLUNAS_DOS_ORIGINADOS.sdr}>{linha.sdr}</CampoDoCartao>
      <CampoDoCartao rotulo={COLUNAS_DOS_ORIGINADOS.responsavel}>{linha.responsavel}</CampoDoCartao>
      <CampoDoCartao rotulo={COLUNAS_DOS_ORIGINADOS.inicio}>
        <span className="numero">{linha.inicio}</span>
      </CampoDoCartao>
      <CampoDoCartao rotulo={COLUNAS_DOS_ORIGINADOS.conclusao}>
        <span className="numero">{linha.conclusao}</span>
      </CampoDoCartao>
      <CampoDoCartao rotulo={COLUNAS_DOS_ORIGINADOS.motivo}>{linha.motivo}</CampoDoCartao>
    </li>
  )
}

export function ListaDeOriginados() {
  const ehCelular = useEhCelular()
  const consulta = useOriginados()
  const { data: sessao } = useSessao()
  /* Os nomes da SDR e do Responsável vêm da equipe (AD-25): o serviço manda só os uuids. */
  const equipe = useResponsaveis()
  const nomesDaEquipe = useMemo(
    () => new Map((equipe.data ?? []).map((pessoa) => [pessoa.id, pessoa.nome])),
    [equipe.data],
  )

  const itens = useMemo(() => consulta.data ?? [], [consulta.data])

  /* Um pedido em lote para a tela inteira, e não um por linha (AD-25). */
  const idsDeEmpresas = useMemo(
    () => [...new Set(itens.flatMap((item) => (item.empresa_id === null ? [] : [item.empresa_id])))],
    [itens],
  )
  const empresas = useNomesDeEmpresas(idsDeEmpresas)

  /*
   * O vocabulário **ativo** dos Motivos, memoizado: o `?? []` cru cria um array novo a cada
   * renderização, e ele é dependência do `useMemo` dos ids avulsos logo abaixo — o cálculo rodaria
   * em toda renderização, e a lista de ids pedidos a `useRotulosDeOpcoes` mudaria de identidade
   * junto. É o mesmo cuidado de `FichaDoNegocio`.
   */
  const opcoes = useOpcoesDeDominio()
  const motivos = useMemo(() => opcoes.data?.motivo_de_perda ?? [], [opcoes.data])
  /*
   * O vocabulário ainda está no ar? — a guarda do degrau de baixo.
   *
   * Enquanto ele não chega, `motivos` é `[]` e **todo** Motivo gravado pareceria avulso: a
   * primeira pintura dispararia um `GET /opcoes-de-dominio/{id}` por linha que a segunda descarta,
   * e numa lista longa isso é uma rajada de pedidos que a tela nunca lê. `isPending` — e não
   * `isError` — porque a recusa do vocabulário é o caso em que os pedidos avulsos são a **única**
   * chance de a linha ter rótulo: quem falhou foi a lista inteira, e não o id.
   */
  const vocabularioAcaminho = opcoes.isPending

  /*
   * O segundo degrau da escada: os Motivos que o vocabulário ativo **não** resolve — os que a
   * gestão excluiu depois de a perda ter sido registrada, e que continuam gravados (FR-67). Sem
   * eles, a coluna mostraria o texto genérico num Motivo que o serviço sabe nomear.
   */
  const idsAvulsos = useMemo(() => {
    if (vocabularioAcaminho) {
      return []
    }
    const gravados = new Set(
      itens
        .map((item) => item.motivo_perda_id)
        .filter((id): id is string => id !== null && !motivos.some((opcao) => opcao.id === id)),
    )
    return [...gravados]
  }, [itens, motivos, vocabularioAcaminho])
  const rotulosAvulsos = useRotulosDeOpcoes(idsAvulsos)

  const nomeDe = useCallback(
    (id: string): string =>
      id === sessao?.usuario_id ? sessao.nome : (nomesDaEquipe.get(id) ?? NOME_INDISPONIVEL),
    [sessao, nomesDaEquipe],
  )

  const linhas: LinhaDoOriginado[] = useMemo(
    () =>
      itens.map((item) => {
        const nome = item.empresa_id === null ? undefined : empresas.nomes.get(item.empresa_id)
        /*
         * As causas, separadas. Só `ausente` — lote inteiro chegado e nome fora dele — é a
         * degradação documentada do FR-30. A ordem das guardas é a das certezas: `carregando`
         * antes de `falhou`, porque um lote ainda no ar pode trazer o nome.
         */
        const origemDoTitulo: OrigemDoTitulo =
          item.empresa_id === null
            ? 'sem-empresa'
            : nome !== undefined
              ? 'resolvido'
              : empresas.carregando
                ? 'pendente'
                : empresas.falhou
                  ? 'indisponivel'
                  : 'ausente'
        /*
         * O terceiro degrau é o rótulo genérico, e ele é o **último** recurso: o rótulo de verdade
         * vem do vocabulário ativo ou de `GET /opcoes-de-dominio/{id}`, que devolve ativos e
         * inativos. Mostrar o uuid cru seria entregar um identificador a quem trabalha com nomes.
         */
        const motivo =
          item.motivo_perda_id === null
            ? SEM_MOTIVO_DE_PERDA
            : (motivos.find((opcao) => opcao.id === item.motivo_perda_id)?.rotulo ??
              rotulosAvulsos.get(item.motivo_perda_id) ??
              MOTIVO_FORA_DA_LISTA)
        return {
          id: item.id,
          /*
           * O nome que não veio vira o **código** do Negócio — o identificador que a operação já
           * usa na boca (AD-12). Sem formatador de número: `codigo` é identificador, e não
           * quantidade.
           */
          titulo:
            item.empresa_id === null
              ? EMPRESA_A_DEFINIR
              : (nome ?? `Negócio ${String(item.codigo)}`),
          origemDoTitulo,
          codigo: String(item.codigo),
          /* O nome vem da réplica do serviço (inclui desligados); a equipe lida é o plano B. */
          sdr: item.sdr_nome ?? nomeDe(item.sdr_id),
          responsavel: item.responsavel_nome ?? nomeDe(item.responsavel_id),
          inicio: DATA.format(new Date(item.data_de_inicio)),
          /* O travessão é o **mesmo** da coluna do Motivo, e por isso é derivado dela. */
          conclusao:
            item.data_de_conclusao === null
              ? SEM_DATA_DE_CONCLUSAO
              : DATA.format(new Date(item.data_de_conclusao)),
          status: item.status,
          /*
           * O plano B é o valor **cru**, e ele existe apesar de o tipo prometer os três: o
           * `status` chega da rede, e o tipo gerado descreve o contrato de hoje — um valor novo no
           * serviço deixaria o chip vazio, e a linha perderia justamente o desfecho que o FR-35
           * promete. O `Record` continua exigindo os três no build; isto é o piso de runtime.
           */
          desfecho:
            (ROTULO_DO_STATUS as Record<string, string | undefined>)[item.status] ?? item.status,
          motivo,
        }
      }),
    [
      itens,
      empresas.nomes,
      empresas.carregando,
      empresas.falhou,
      motivos,
      rotulosAvulsos,
      nomeDe,
    ],
  )

  /*
   * O `!isError` na frente importa: numa recusa `consulta.data` também é `undefined`, e sem ele o
   * erro seria lido como "ainda carregando" — a tela sumiria e quem tomou um `500` ficaria sem
   * saída. É a mesma fórmula de `PainelDeHoje`, de `QuadroDoFunil` e de `ListaDeEmpresas`.
   */
  const carregando = !consulta.isError && (consulta.isLoading || consulta.data === undefined)
  const vazio = !carregando && !consulta.isError && linhas.length === 0

  /*
   * A ordem dos estados é erro → carregando → vazio → celular → desktop, e ela é a mesma das
   * outras telas do módulo. O erro vem primeiro porque é o único estado que oferece uma **saída**;
   * o vazio vem depois de `carregando` porque uma lista ainda no ar também tem zero linhas, e
   * escrever "você não originou nada" ali seria a tela afirmando o que ainda não sabe.
   */
  return (
    <div className="originados">
      {/* O cartão do protótipo v2: o título e, à direita, a nota do recorte sem valor (FR-62). */}
      <section className="originados__cartao" aria-label={TITULO_DO_CARTAO}>
      <div className="originados__cartao-cabecalho">
        <h2 className="originados__cartao-titulo">{TITULO_DO_CARTAO}</h2>
        <span className="originados__cartao-nota">{NOTA_DO_CARTAO}</span>
      </div>
      {consulta.isError ? (
        /* Verbatim: a recusa do serviço é a que sabe explicar o caso. */
        <div className="originados__recusa">
          <p className="originados__erro" role="alert">
            {mensagemDe(consulta.error)}
          </p>
          <button
            type="button"
            className="originados__botao"
            onClick={() => {
              void consulta.refetch()
            }}
          >
            {TENTAR_DE_NOVO}
          </button>
        </div>
      ) : carregando ? (
        <p className="originados__aviso" role="status">
          {CARREGANDO}
        </p>
      ) : vazio ? (
        <p className="originados__aviso">{SEM_ORIGINADOS}</p>
      ) : (
        <>
          {/* O aviso vem **junto** com as linhas, e não no lugar delas: a lista chegou, e o que
              falhou foi só o nome. */}
          {empresas.falhou ? (
            <p className="originados__aviso originados__aviso--recusa" role="alert">
              {NOMES_NAO_VIERAM}
            </p>
          ) : null}
          {ehCelular ? (
            <ul className="originados__cartoes" aria-label={NOME_DA_LISTA}>
              {linhas.map((linha) => (
                <CartaoDoOriginado key={linha.id} linha={linha} />
              ))}
            </ul>
          ) : (
            <table className="originados__tabela">
              <caption className="originados__so-para-leitor">{NOME_DA_LISTA}</caption>
              <thead>
                <tr>
                  <th scope="col" className="originados__cabecalho">
                    {COLUNAS_DOS_ORIGINADOS.empresa}
                  </th>
                  <th scope="col" className="originados__cabecalho">
                    {COLUNAS_DOS_ORIGINADOS.codigo}
                  </th>
                  <th scope="col" className="originados__cabecalho">
                    {COLUNAS_DOS_ORIGINADOS.sdr}
                  </th>
                  <th scope="col" className="originados__cabecalho">
                    {COLUNAS_DOS_ORIGINADOS.responsavel}
                  </th>
                  <th scope="col" className="originados__cabecalho">
                    {COLUNAS_DOS_ORIGINADOS.inicio}
                  </th>
                  <th scope="col" className="originados__cabecalho">
                    {COLUNAS_DOS_ORIGINADOS.conclusao}
                  </th>
                  <th scope="col" className="originados__cabecalho">
                    {COLUNAS_DOS_ORIGINADOS.desfecho}
                  </th>
                  <th scope="col" className="originados__cabecalho">
                    {COLUNAS_DOS_ORIGINADOS.motivo}
                  </th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((linha) => (
                  <LinhaDaTabela key={linha.id} linha={linha} />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      </section>
    </div>
  )
}
